import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Define the ApprovalTask interface
export interface ApprovalTask {
  id: string;
  workflow_transaction: {
    id: string;
    transaction_id: string;
    form_name: string;
    name: string;
    category: string;
    event_type: string;
    initiated_date: string;
    initiated_by: {
      id: string;
      name: string;
    };
  };
  approval_level: {
    level_number: number;
    description: string;
    approval_mode?: 'user_role' | 'specific_user' | string;
  };
  assigned_to_role: string | null;
  assigned_to_user: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  status_display: string;
  comments: string;
  assigned_date: string;
  due_date: string | null;
  completed_date: string | null;
  is_overdue: boolean;
}

// Pagination interface for approval tasks
export interface ApprovalTaskPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApprovalTask[];
}

// Filter parameters for approval tasks
export interface ApprovalTaskFilterParams {
  id?: string;
  status?: 'pending' | 'approved' | 'rejected' | '';
  form_name?: string;
  name?: string;
  category?: string;
  event_type?: string;
  initiated_by?: string;
  date_from?: string;
  date_to?: string;
  transaction_id?: string;
  completed_date_from?: string;
  completed_date_to?: string;
  ordering?: string;
  page?: number;
  limit?: number;
}

export interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
}

// A user with the "Override task" permission (reassignment target)
export interface OverrideUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface Comment {
  id: string;
  user: User;
  comment: string;
  created_date: string;
}

export interface Attachment {
  id: string;
  filename: string;
  file_type: string;
  file_size: number;
  uploaded_by: User;
  upload_date: string;
  description: string;
  file_url: string | null;
}

export interface PreviousApproval {
  id: string;
  level: number;
  status: string;
  status_display: string;
  completed_date: string | null;
  completed_by?: User | null;
  comments: string;
}

export interface DataChanges {
  field: string;
  old_value: string | null;
  new_value: string | null;
}

export interface ApprovalTaskDetails {
  task: {
    id: string;
    status: 'pending' | 'approved' | 'rejected' | string;
    status_display: string;
    assigned_date: string;
    due_date: string | null;
    completed_date: string | null;
    is_current: boolean;
    comments: string;
    assigned_to_role: string;
    assigned_to_user: {
      id: string;
      email: string;
      full_name: string;
      username: string;
    }
  };
  approval_level: {
    id: string;
    level: number;
    description: string;
    approval_mode: 'user_role' | 'specific_user' | string;
    approval_mode_display: string;
    approval_type: 'sequential';
    approval_type_display: string;
    modifiable_fields: string[];
  };
  data_changes: DataChanges[];
  workflow_transaction: {
    id: string;
    transaction_id: string;
    status: 'pending' | 'approved' | 'rejected' | string;
    status_display: string;
    current_approval_level: number;
    submission_date: string;
    estimated_completion_date: string | null;
    completion_date: string | null;
    form_name: string;
    form_data: {
      name: string;
      site: string;
      description: string;
      location_type: string;
      parent_location: string;
    };
    form_data_snapshot: {
      name: string;
      site: string;
      description: string;
      location_type: string;
      parent_location: string | null;
    };
    initiated_by: {
      id: string;
      name: string;
    };
    category: string;
  };
  workflow_template: {
    id: string;
    name: string;
    description: string;
    event_type: 'create' | 'update' | string;
    entity_type: string;
    event_type_display: string;
    priority: number;
    priority_display: string;
  };
  comments: Comment[];
  attachments: Attachment[];
  previous_approvals: PreviousApproval[];
}


// Create API
export const approvalTaskApi = createApi({
  reducerPath: 'approvalTaskApi',
  baseQuery: createExtendedTokenBaseQuery('workflows/'),
  tagTypes: ['ApprovalTask', 'WorkflowTransaction'],
  endpoints: (builder) => ({
    // Get approval tasks for current user
    getApprovalTasks: builder.query<ApprovalTaskPaginatedResponse, ApprovalTaskFilterParams | void>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
              queryParams.append(key, value.toString());
            }
          });
        }
        return { url: 'approval-tasks/', params: queryParams };
      },
      providesTags: (result) => 
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'ApprovalTask' as const, id })),
              { type: 'ApprovalTask', id: 'LIST' },
            ]
          : [{ type: 'ApprovalTask', id: 'LIST' }],
    }),
    
    // Get approval task by ID
    getApprovalTaskById: builder.query<ApprovalTaskDetails, string>({
      query: (id) => `approval-tasks/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ApprovalTask', id }],
    }),
    
    // Get the users an approval task can be reassigned to (users with the "Override task" permission)
    getOverrideUsers: builder.query<{ results: OverrideUser[] }, void>({
      query: () => 'approval-tasks/override-users/',
      providesTags: [{ type: 'ApprovalTask', id: 'OVERRIDE_USERS' }],
    }),

    // Get count of pending approval tasks
    getPendingTaskCount: builder.query<{ count: number }, void>({
      query: () => 'approval-tasks/count/',
      providesTags: [{ type: 'ApprovalTask', id: 'COUNT' }],
    }),
    
    // Approve task
    approveTask: builder.mutation<ApprovalTask, { id: string; comments?: string; workflowTransactionId?: string }>({
      query: ({ id, comments }) => ({
        url: `approval-tasks/${id}/approve/`,
        method: 'POST',
        body: { comments },
      }),
      // Note: workflowTransactionId is only used for client-side cache invalidation, not sent to API
      invalidatesTags: (result, error, { id, workflowTransactionId }) => [
        { type: 'ApprovalTask', id },
        { type: 'ApprovalTask', id: 'LIST' },
        { type: 'ApprovalTask', id: 'COUNT' },
        // Also invalidate WorkflowTransaction tags to refresh transaction data
        { type: 'WorkflowTransaction', id: 'LIST' },
        ...(workflowTransactionId ? [{ type: 'WorkflowTransaction' as const, id: workflowTransactionId }] : []),
      ],
    }),
    
    // Reject task
    rejectTask: builder.mutation<ApprovalTask, { id: string; comments: string; workflowTransactionId?: string }>({
      query: ({ id, comments }) => ({
        url: `approval-tasks/${id}/reject/`,
        method: 'POST',
        body: { comments },
      }),
      // Note: workflowTransactionId is only used for client-side cache invalidation, not sent to API
      invalidatesTags: (result, error, { id, workflowTransactionId }) => [
        { type: 'ApprovalTask', id },
        { type: 'ApprovalTask', id: 'LIST' },
        { type: 'ApprovalTask', id: 'COUNT' },
        // Also invalidate WorkflowTransaction tags to refresh transaction data
        { type: 'WorkflowTransaction', id: 'LIST' },
        ...(workflowTransactionId ? [{ type: 'WorkflowTransaction' as const, id: workflowTransactionId }] : []),
      ],
    }),
    
    // Reassign task: user tasks go to a user, role tasks go to a role (send exactly one)
    reassignTask: builder.mutation<
      ApprovalTask,
      { id: string; assigned_to_user?: string; assigned_to_role?: string; reason: string; workflowTransactionId?: string }
    >({
      query: ({ id, assigned_to_user, assigned_to_role, reason }) => ({
        url: `approval-tasks/${id}/reassign/`,
        method: 'POST',
        body: { assigned_to_user, assigned_to_role, reason },
      }),
      // Note: workflowTransactionId is only used for client-side cache invalidation, not sent to API
      invalidatesTags: (result, error, { id, workflowTransactionId }) => [
        { type: 'ApprovalTask', id },
        { type: 'ApprovalTask', id: 'LIST' },
        // Also invalidate WorkflowTransaction tags to refresh transaction data
        { type: 'WorkflowTransaction', id: 'LIST' },
        ...(workflowTransactionId ? [{ type: 'WorkflowTransaction' as const, id: workflowTransactionId }] : []),
      ],
    }),

    // Cancel the workflow transaction of a task (requires the "Override task" permission)
    cancelTask: builder.mutation<ApprovalTask, { id: string; comments?: string; workflowTransactionId?: string }>({
      query: ({ id, comments }) => ({
        url: `approval-tasks/${id}/cancel/`,
        method: 'POST',
        body: { comments },
      }),
      // Note: workflowTransactionId is only used for client-side cache invalidation, not sent to API
      invalidatesTags: (result, error, { id, workflowTransactionId }) => [
        { type: 'ApprovalTask', id },
        { type: 'ApprovalTask', id: 'LIST' },
        { type: 'ApprovalTask', id: 'COUNT' },
        // Also invalidate WorkflowTransaction tags to refresh transaction data
        { type: 'WorkflowTransaction', id: 'LIST' },
        ...(workflowTransactionId ? [{ type: 'WorkflowTransaction' as const, id: workflowTransactionId }] : []),
      ],
    }),
    
    // Bulk approve tasks
    bulkApproveTasks: builder.mutation<{ success: number; failed: number }, { ids: string[]; comments?: string }>({
      query: ({ ids, comments }) => ({
        url: 'approval-tasks/bulk-approve/',
        method: 'POST',
        body: { ids, comments },
      }),
      invalidatesTags: [
        { type: 'ApprovalTask', id: 'LIST' },
        { type: 'ApprovalTask', id: 'COUNT' },
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetApprovalTasksQuery,
  useGetApprovalTaskByIdQuery,
  useGetOverrideUsersQuery,
  useGetPendingTaskCountQuery,
  useApproveTaskMutation,
  useRejectTaskMutation,
  useReassignTaskMutation,
  useCancelTaskMutation,
  useBulkApproveTasksMutation,
} = approvalTaskApi;
